import React, { useState, useEffect, useMemo } from 'react';
import { noteApi, searchApi } from '../utils/api';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, ButtonGroup, Row, Col, Spinner, Alert } from 'reactstrap';
import { useKeycloak } from '@react-keycloak/web';
import {
    Sparkles,
    List,
    Grid3X3,
    LayoutGrid,
    Trash2,
    Plus,
    RotateCw,
    Search,
    SlidersHorizontal,
    CalendarCheck,
    AlertTriangle,
    Brain,
    X
} from 'lucide-react';
import NoteCard from '../components/NoteCard';
import CalendarSidebar from '../components/CalendarSidebar';

const NoteDashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { keycloak } = useKeycloak();
    
    const [notes, setNotes] = useState([]);
    const [stats, setStats] = useState(null);
    const [uploading, setUploading] = useState(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState(localStorage.getItem('sb_view_mode') || 'grid');
    
    const [selectedIds, setSelectedIds] = useState([]);
    const [deletedCount, setDeletedCount] = useState(0);
    const [dashboardSearch, setDashboardSearch] = useState('');

    // Alerts for today & overdue tasks
    const [todayNotesCount, setTodayNotesCount] = useState(0);
    const [overdueNotesCount, setOverdueNotesCount] = useState(0);

    // AI Summary state
    const [aiLoading, setAiLoading] = useState(false);
    const [aiResponse, setAiResponse] = useState(null);
    const [aiError, setAiError] = useState(null);

    // Sprawdzanie czy wróciliśmy po usunięciu notatki
    useEffect(() => {
        if (location.state?.deleted) {
            setDeletedCount(1);
            window.history.replaceState({}, document.title);
            setTimeout(() => setDeletedCount(0), 10000);
        }
    }, [location]);

    // Automatyczne odświeżanie przy zmianie trybu widoku
    useEffect(() => {
        fetchNotes(0, true);
    }, [viewMode]);

    const firstName = useMemo(() => {
        return keycloak?.idTokenParsed?.given_name 
            || keycloak?.idTokenParsed?.preferred_username 
            || t('user_fallback');
    }, [keycloak?.idTokenParsed, t]);

    useEffect(() => {
        fetchNotes(0, true);
        fetchStats();
        fetchNoteAlerts();
    }, []);

    const fetchNoteAlerts = async () => {
        try {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const dateString = `${year}-${month}-${day}`;

            const resToday = await searchApi.get('', {
                params: {
                    targetFrom: `${dateString}T00:00`,
                    targetTo: `${dateString}T23:59`,
                    completed: false,
                    size: 1
                }
            });
            setTodayNotesCount(resToday.data.totalElements || 0);

            const resOverdue = await searchApi.get('', {
                params: {
                    targetTo: `${dateString}T00:00`,
                    completed: false,
                    size: 1
                }
            });
            setOverdueNotesCount(resOverdue.data.totalElements || 0);
        } catch (err) {
            console.error("Failed to fetch note alerts", err);
        }
    };

    const navigateToToday = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        navigate(`/advanced-search?targetFrom=${dateString}T00:00&targetTo=${dateString}T23:59&completed=false`);
    };

    const navigateToOverdue = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        navigate(`/advanced-search?targetTo=${dateString}T00:00&completed=false`);
    };

    const changeViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('sb_view_mode', mode);
    };

    const fetchStats = async () => {
        try {
            const res = await noteApi.get('stats');
            setStats(res.data);
        } catch (err) {
            console.error("Failed to fetch stats", err);
        }
    };

    const fetchAiSummary = async () => {
        setAiLoading(true);
        setAiResponse('');
        setAiError(null);
        try {
            if (keycloak?.updateToken) {
                try {
                    await keycloak.updateToken(30);
                } catch (e) {
                    console.warn("Token refresh skipped", e);
                }
            }
            const headers = {};
            if (keycloak?.token) {
                headers.Authorization = `Bearer ${keycloak.token}`;
            }

            const response = await fetch('/backend/api/ai/notesSummary', {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (!response.body) {
                const fullText = await response.text();
                setAiResponse(fullText);
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let done = false;

            while (!done) {
                const { value, done: readerDone } = await reader.read();
                done = readerDone;
                if (value) {
                    const chunkText = decoder.decode(value, { stream: !done });
                    if (chunkText) {
                        setAiResponse(prev => (prev || '') + chunkText);
                    }
                }
            }
        } catch (err) {
            console.error("Failed to fetch AI summary", err);
            setAiError(t('ai_error'));
        } finally {
            setAiLoading(false);
        }
    };

    const fetchNotes = async (pageNumber = 0, replace = false) => {
        setLoading(true);
        if (replace) {
            setNotes([]);
            setHasMore(false);
        }

        try {
            let size = 12;
            if (viewMode === 'small') size = 24;
            if (viewMode === 'list') size = 20;

            const res = await noteApi.get('', {
                params: {
                    page: pageNumber,
                    size: size,
                    sort: 'createdAt,desc'
                }
            });
            
            const content = res.data.content || (Array.isArray(res.data) ? res.data : []);
            
            let isLast = true;
            if (res.data.page) {
                isLast = res.data.page.number + 1 >= res.data.page.totalPages;
            } else if (res.data.last !== undefined) {
                isLast = res.data.last;
            } else {
                isLast = content.length < size;
            }

            if (replace) {
                setNotes(content);
                setSelectedIds([]);
                setHasMore(!isLast);
            } else {
                setNotes(prev => {
                    const updated = [...prev, ...content];
                    setHasMore(!isLast);
                    return updated;
                });
            }
            
            setPage(pageNumber);
        } catch (err) {
            console.error("Fetch failed:", err);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleCardClick = (id) => {
        if (selectedIds.length > 0) {
            toggleSelect(id);
        } else {
            navigate(`/edit/${id}`);
        }
    };

    const handleDeleteSelected = async () => {
        const count = selectedIds.length;
        if (window.confirm(t('confirm_delete_plural', { count }))) {
            try {
                await noteApi.delete('', { data: selectedIds });
                
                setNotes(prev => prev.filter(note => !selectedIds.includes(note.id)));
                setDeletedCount(count);
                setSelectedIds([]);
                
                window.dispatchEvent(new CustomEvent('notes-updated'));
                fetchStats();
                fetchNoteAlerts();
                
                setTimeout(() => setDeletedCount(0), 10000);
            } catch (err) {
                console.error("Failed to delete notes", err);
            }
        }
    };

    const handleFileUploadOnList = async (noteId, files) => {
        if (!files || files.length === 0) return;
        setUploading(noteId);
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                await noteApi.post(`${noteId}/attachments`, formData);
            }
            fetchNotes(0, true);
        } catch (err) {
            console.error("Upload failed", err);
        } finally {
            setUploading(null);
        }
    };

    const handleDashboardSearchSubmit = (e) => {
        e.preventDefault();
        const q = dashboardSearch.trim();
        if (q) {
            navigate(`/advanced-search?q=${encodeURIComponent(q)}`);
        } else {
            navigate('/advanced-search?showAll=true');
        }
    };

    // Filtered notes when typing in the dashboard quick filter
    const filteredNotes = useMemo(() => {
        const q = dashboardSearch.trim().toLowerCase();
        if (!q) return notes;
        return notes.filter(n =>
            (n.title && n.title.toLowerCase().includes(q)) ||
            (n.content && n.content.toLowerCase().includes(q)) ||
            (Array.isArray(n.tags) && n.tags.some(tag => tag.toLowerCase().includes(q)))
        );
    }, [notes, dashboardSearch]);

    const getColProps = () => {
        if (viewMode === 'list') return { xs: 12 };
        if (viewMode === 'small') return { xs: 6, sm: 4, md: 3, lg: 2 };
        return { xs: 12, sm: 6, lg: 4 };
    };

    return (
        <div className="container mt-4 pb-5 animate-fade-in">
            {/* Hero Section */}
            <div className="hero-section p-4 rounded-4 shadow-sm mb-4 position-relative overflow-hidden">
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                        <div>
                            <h1 className="h2 fw-bold mb-1">{t('welcome_user', { name: firstName })}</h1>
                            <div className="text-muted mb-0">
                                {stats ? (
                                    <>{t('you_have_notes_prefix')} <strong className="text-primary">{stats.totalNotes}</strong> {t('you_have_notes_suffix')}</>
                                ) : (
                                    <Spinner size="sm" color="primary" />
                                )}
                            </div>
                        </div>

                        <div className="d-flex flex-wrap align-items-center gap-2">
                            {todayNotesCount > 0 && (
                                <div
                                    className="btn btn-sm btn-success rounded-pill px-3 py-2 shadow-sm d-inline-flex align-items-center gap-2 fw-semibold"
                                    onClick={navigateToToday}
                                    title={t('tasks_today')}
                                >
                                    <CalendarCheck size={16} />
                                    <span>
                                        {t('tasks_today')}:{' '}
                                        <Trans i18nKey="tasks_today_count" count={todayNotesCount} components={{ bold: <strong /> }} />
                                    </span>
                                </div>
                            )}

                            {overdueNotesCount > 0 && (
                                <div
                                    className="btn btn-sm btn-danger rounded-pill px-3 py-2 shadow-sm d-inline-flex align-items-center gap-2 fw-semibold"
                                    onClick={navigateToOverdue}
                                    title={t('tasks_overdue')}
                                >
                                    <AlertTriangle size={16} />
                                    <span>
                                        {t('tasks_overdue')}:{' '}
                                        <Trans i18nKey="tasks_overdue_count" count={overdueNotesCount} components={{ bold: <strong /> }} />
                                    </span>
                                </div>
                            )}

                            <Button
                                color="outline-primary"
                                onClick={fetchAiSummary}
                                disabled={aiLoading}
                                className="rounded-pill px-3 py-2 shadow-sm fw-bold btn-theme-aware d-inline-flex align-items-center gap-2"
                            >
                                {aiLoading ? (
                                    <>
                                        <Spinner size="sm" color="primary" />
                                        <span>{t('ai_analyzing')}</span>
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={16} className="text-primary" />
                                        <span>{t('ask_ai_button').replace(/^🤖\s*/, '')}</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* AI Response */}
                    {(aiLoading || aiResponse || aiError) && (
                        <div className="mt-3 p-3 rounded-4 shadow-sm" style={{ backgroundColor: 'var(--sb-card-bg)', border: '1px solid var(--sb-border)' }}>
                            {aiLoading && !aiResponse && (
                                <div className="d-flex align-items-center text-primary">
                                    <Spinner size="sm" color="primary" className="me-2" />
                                    <span>{t('ai_analyzing')}</span>
                                </div>
                            )}
                            {aiError && (
                                <Alert color="danger" className="mb-0 rounded-4">
                                    ⚠️ {aiError}
                                </Alert>
                            )}
                            {aiResponse && (
                                <div>
                                    <h6 className="fw-bold text-primary mb-2 d-flex align-items-center gap-2">
                                        <Sparkles size={16} />
                                        <span>{t('ai_response_label')}</span>
                                        {aiLoading && <Spinner size="sm" color="primary" className="ms-1" />}
                                    </h6>
                                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                        {aiResponse}
                                        {aiLoading && <span className="text-primary ms-1">▍</span>}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Sticky Toolbar with Inline Search & Controls */}
            <div className="d-flex flex-nowrap justify-content-between align-items-center gap-2 mb-4 py-2 px-2 px-sm-3 rounded-4 shadow-sm sticky-top border-custom" style={{ top: '56px', zIndex: 100 }}>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <h5 className="mb-0 fw-bold text-nowrap d-none d-md-block">
                        {selectedIds.length > 0 ? (
                            <span className="text-primary">{t('selected_count', { count: selectedIds.length })}</span>
                        ) : (
                            t('your_notes')
                        )}
                    </h5>
                    {selectedIds.length > 0 && (
                        <span className="badge bg-primary rounded-pill d-md-none">{selectedIds.length}</span>
                    )}
                    
                    {/* View Switcher */}
                    <ButtonGroup size="sm" className="ms-0 ms-md-1">
                        <Button 
                            color="light" 
                            active={viewMode === 'list'} 
                            onClick={() => changeViewMode('list')}
                            className="border shadow-none btn-theme-aware d-inline-flex align-items-center px-2 py-1"
                            title={t('view_list')}
                        >
                            <List size={16} />
                        </Button>
                        <Button 
                            color="light" 
                            active={viewMode === 'small'} 
                            onClick={() => changeViewMode('small')}
                            className="border shadow-none btn-theme-aware d-inline-flex align-items-center px-2 py-1"
                            title={t('view_small_grid')}
                        >
                            <Grid3X3 size={16} />
                        </Button>
                        <Button 
                            color="light" 
                            active={viewMode === 'grid'} 
                            onClick={() => changeViewMode('grid')}
                            className="border shadow-none btn-theme-aware d-inline-flex align-items-center px-2 py-1"
                            title={t('view_large_grid')}
                        >
                            <LayoutGrid size={16} />
                        </Button>
                    </ButtonGroup>
                </div>

                {/* Inline Search in Notes Toolbar */}
                <form
                    onSubmit={handleDashboardSearchSubmit}
                    className="sb-top-search flex-grow-1 my-0"
                    style={{ maxWidth: '460px', minWidth: '110px' }}
                >
                    <Search size={16} className="text-muted flex-shrink-0 d-none d-sm-inline" />
                    <input
                        type="search"
                        placeholder={t('search_placeholder')}
                        value={dashboardSearch}
                        onChange={e => setDashboardSearch(e.target.value)}
                    />
                    {dashboardSearch && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-muted p-0 me-1 me-sm-2 d-inline-flex align-items-center flex-shrink-0"
                            onClick={() => setDashboardSearch('')}
                            title={t('clear')}
                        >
                            <X size={15} />
                        </button>
                    )}
                    <button
                        type="submit"
                        className="btn btn-sm btn-primary rounded-pill px-2 px-md-3 py-1 fw-semibold d-inline-flex align-items-center justify-content-center gap-1 flex-shrink-0"
                        title={t('search_button')}
                    >
                        <Search size={14} className="flex-shrink-0" />
                        <span className="d-none d-md-inline small">{t('search_button')}</span>
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-light rounded-pill px-2 py-1 ms-1 border btn-theme-aware d-inline-flex align-items-center justify-content-center flex-shrink-0"
                        onClick={() => navigate(`/advanced-search${dashboardSearch.trim() ? `?q=${encodeURIComponent(dashboardSearch.trim())}` : ''}`)}
                        title={t('advanced_search_title')}
                    >
                        <SlidersHorizontal size={14} />
                    </button>
                </form>

                <div className="d-flex flex-nowrap justify-content-end gap-1 gap-sm-2 flex-shrink-0">
                    {selectedIds.length > 0 && (
                        <Button
                            color="danger"
                            outline
                            onClick={handleDeleteSelected}
                            className="rounded-pill px-2 px-lg-3 shadow-sm fw-bold btn-sm d-inline-flex align-items-center justify-content-center gap-1 text-nowrap"
                            title={t('delete_selected')}
                        >
                            <Trash2 size={15} className="flex-shrink-0" />
                            <span className="d-none d-lg-inline">{t('delete_selected')}</span>
                        </Button>
                    )}
                    <Button
                        color="primary"
                        onClick={() => navigate('/new-note')}
                        className="rounded-pill px-2 px-lg-3 shadow-sm fw-bold btn-sm d-inline-flex align-items-center justify-content-center gap-1 text-nowrap"
                        title={t('new_note_title')}
                    >
                        <Plus size={16} className="flex-shrink-0" />
                        <span className="d-none d-lg-inline">{t('new_note_title')}</span>
                    </Button>
                </div>
            </div>

            {/* Main Content with Sidebar */}
            <Row>
                <Col md="9">
                    {deletedCount > 0 && (
                        <Alert color="info" toggle={() => setDeletedCount(0)} className="shadow-sm border-0 rounded-4 mb-4 fade show">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <Trash2 size={20} />
                                    <span>
                                        {t('deletion_success_message', { count: deletedCount })}
                                    </span>
                                </div>
                                <Button 
                                    size="sm" 
                                    color="info" 
                                    outline 
                                    onClick={() => { fetchNotes(0, true); setDeletedCount(0); }}
                                    className="rounded-pill fw-bold border-2 px-3 d-inline-flex align-items-center gap-1"
                                >
                                    <RotateCw size={14} />
                                    <span>{t('refresh_list')}</span>
                                </Button>
                            </div>
                        </Alert>
                    )}
                    {/* Notes Layout */}
                    <Row className={viewMode === 'list' ? 'g-2' : 'g-3 g-md-4'}>
                        {filteredNotes.map(note => (
                            <Col key={note.id} {...getColProps()}>
                                <NoteCard 
                                    note={note}
                                    isSelected={selectedIds.includes(note.id)}
                                    onSelect={toggleSelect}
                                    onClick={handleCardClick}
                                    onFileUpload={handleFileUploadOnList}
                                    uploading={uploading}
                                    viewMode={viewMode}
                                />
                            </Col>
                        ))}
                    </Row>

                    {/* Empty State when filter doesn't match local notes */}
                    {!loading && notes.length > 0 && filteredNotes.length === 0 && (
                        <div className="text-center py-5 rounded-4 border border-custom bg-card-custom my-3">
                            <Search size={42} className="text-muted mb-3 opacity-50" />
                            <h5 className="fw-bold mb-2">{t('search_results')}: 0</h5>
                            <p className="text-muted small mb-3">
                                {t('search_in_elasticsearch_prompt')}
                            </p>
                            <Button
                                color="primary"
                                className="rounded-pill px-4 fw-bold d-inline-flex align-items-center gap-2"
                                onClick={handleDashboardSearchSubmit}
                            >
                                <Search size={16} />
                                <span>{t('search_button')}: &ldquo;{dashboardSearch}&rdquo;</span>
                            </Button>
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && notes.length === 0 && (
                        <div className="text-center py-5">
                            <div className="text-primary mb-3 opacity-50">
                                <Brain size={64} />
                            </div>
                            <h3>{t('empty_brain')}</h3>
                            <p className="text-muted">{t('empty_brain_subtitle')}</p>
                            <Button color="primary" onClick={() => navigate('/new-note')} className="mt-3 px-4 py-2 rounded-pill d-inline-flex align-items-center gap-2 fw-bold">
                                <Plus size={18} />
                                <span>{t('new_note_title')}</span>
                            </Button>
                        </div>
                    )}

                    {/* Load More or End of List CTA */}
                    <div className="text-center mt-5 mb-5">
                        {loading ? (
                            <Spinner color="primary" />
                        ) : hasMore ? (
                            <Button color="outline-primary" size="lg" outline onClick={() => fetchNotes(page + 1)} className="rounded-pill px-5 shadow-sm">
                                {t('show_more')}
                            </Button>
                        ) : notes.length > 0 && (
                            <div className="animate-fade-in py-4 border-top border-custom">
                                <p className="text-muted small mb-3">✨ {t('reached_end_of_brain')}</p>
                                <Button color="primary" size="lg" onClick={() => navigate('/new-note')} className="rounded-pill px-4 py-2 shadow-sm fw-bold d-inline-flex align-items-center gap-2">
                                    <Plus size={18} />
                                    <span>{t('new_note_title')}</span>
                                </Button>
                            </div>
                        )}
                    </div>
                </Col>

                <Col md="3" className="d-none d-md-block">
                    <CalendarSidebar />
                </Col>
            </Row>

            <style>
                {`
                .text-truncate-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .ring-primary {
                    box-shadow: 0 0 0 3px rgba(var(--sb-primary-rgb), 0.25)!important;
                }
                .bg-card-custom { background-color: var(--sb-card-bg)!important; }
                .border-custom { border-color: var(--sb-border)!important; }
                .btn-theme-aware { 
                    background-color: var(--sb-card-bg)!important; 
                    color: var(--sb-text)!important; 
                    border-color: var(--sb-border)!important; 
                }
                .btn-theme-aware:hover {
                    background-color: var(--sb-bg)!important;
                }
                .btn-theme-aware.active {
                    background-color: var(--sb-primary)!important;
                    color: white!important;
                    border-color: var(--sb-primary)!important;
                }
                `}
            </style>
        </div>
    );
};

export default NoteDashboard;
